/**
 * PlayerController — owns the local player's transform, camera, and avatar.
 *
 * Movement model: classic third-person walk with mouse look. Camera orbits
 * the player at a fixed distance and pitch range. WASD moves relative to
 * the camera yaw. Toggle to first-person with V.
 */

import * as THREE from 'three';
import { Avatar } from '@/player/Avatar';
import { Input } from '@/core/Input';
import { PhysicsWorld } from '@/core/PhysicsWorld';
import { dampAngle, clamp } from '@/core/MathUtils';
import { AnimState } from '@shared/protocol';

export type CameraMode = 'third' | 'first';

export class PlayerController {
  readonly avatar: Avatar;
  readonly camera: THREE.PerspectiveCamera;
  readonly position = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();

  yaw = 0;
  pitch = -0.08;
  cameraMode: CameraMode = 'third';
  cameraDistance = 9;
  walkSpeed = 4.5;
  runSpeed = 9.5;
  jumpSpeed = 7.5;

  // Locked emote (set by emote hotkey, releases on movement).
  emoteUntilMove: AnimState | null = null;

  private grounded = false;

  constructor(
    private input: Input,
    private physics: PhysicsWorld,
    camera: THREE.PerspectiveCamera,
    avatar: Avatar
  ) {
    this.camera = camera;
    this.avatar = avatar;
    this.input.on((e) => {
      if (e.type === 'toggle-camera') {
        this.cameraMode = this.cameraMode === 'third' ? 'first' : 'third';
      }
      if (e.type === 'emote') {
        this.emoteUntilMove = e.anim;
      }
    });
  }

  setSpawn(x: number, z: number): void {
    this.position.set(x, this.physics.groundAt(x, z) + 0.05, z);
    this.velocity.set(0, 0, 0);
  }

  /** Compute current animation state from movement + emote lock. */
  currentAnim(): AnimState {
    if (this.emoteUntilMove !== null) return this.emoteUntilMove;
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    if (speed > 6) return AnimState.Running;
    if (speed > 0.4) return AnimState.Walking;
    return AnimState.Idle;
  }

  update(dt: number): void {
    const s = this.input.state;

    // Camera yaw/pitch from mouse delta.
    const md = this.input.consumeMouseDelta();
    this.yaw += md.yaw;
    this.pitch = clamp(this.pitch + md.pitch, -1.2, 0.6);

    // WASD intent vector in camera-local space.
    const fwd = (s.forward ? 1 : 0) - (s.back ? 1 : 0);
    const strafe = (s.right ? 1 : 0) - (s.left ? 1 : 0);
    const moving = fwd !== 0 || strafe !== 0;
    if (moving) this.emoteUntilMove = null;

    // Project intent into world XZ using camera yaw.
    const speed = s.run ? this.runSpeed : this.walkSpeed;
    const cosY = Math.cos(this.yaw);
    const sinY = Math.sin(this.yaw);
    const wx = -fwd * sinY + strafe * cosY;
    const wz = -fwd * cosY - strafe * sinY;
    const len = Math.hypot(wx, wz);
    if (len > 0) {
      this.velocity.x = (wx / len) * speed;
      this.velocity.z = (wz / len) * speed;
    } else {
      this.velocity.x *= 0.4;
      this.velocity.z *= 0.4;
      if (Math.abs(this.velocity.x) < 0.01) this.velocity.x = 0;
      if (Math.abs(this.velocity.z) < 0.01) this.velocity.z = 0;
    }

    // Jump
    if (s.jump && this.grounded) {
      this.velocity.y = this.jumpSpeed;
      this.grounded = false;
    }

    // Resolve against terrain + colliders.
    this.grounded = this.physics.resolve(this.position, this.velocity, dt);

    // Face movement direction in third person.
    if (moving) {
      const targetYaw = Math.atan2(-wx, -wz);
      const current = this.avatar.group.rotation.y;
      this.avatar.group.rotation.y = dampAngle(current, targetYaw, 12, dt);
    }

    this.avatar.group.position.copy(this.position);
    this.avatar.setAnim(this.currentAnim());
    this.avatar.pose(dt, len * speed);

    // Camera follow.
    if (this.cameraMode === 'third') {
      const camOffset = new THREE.Vector3(
        Math.sin(this.yaw) * this.cameraDistance * Math.cos(-this.pitch),
        this.cameraDistance * Math.sin(-this.pitch) + 1.6,
        Math.cos(this.yaw) * this.cameraDistance * Math.cos(-this.pitch)
      );
      const desired = this.position.clone().add(new THREE.Vector3(0, 1.4, 0)).add(camOffset);
      this.camera.position.lerp(desired, 1 - Math.exp(-12 * dt));
      const lookTarget = this.position.clone().add(new THREE.Vector3(0, 1.5, 0));
      this.camera.lookAt(lookTarget);
    } else {
      // First person: camera at head position, look along yaw/pitch.
      this.camera.position.set(this.position.x, this.position.y + 1.65, this.position.z);
      const dir = new THREE.Vector3(
        -Math.sin(this.yaw) * Math.cos(this.pitch),
        Math.sin(this.pitch),
        -Math.cos(this.yaw) * Math.cos(this.pitch)
      );
      this.camera.lookAt(this.camera.position.clone().add(dir));
      // Hide our own avatar in first person.
      this.avatar.group.visible = false;
      return;
    }
    this.avatar.group.visible = true;
  }
}
